<template>
  <div class="card">
    <h4 class="card-header">{{ t('edit') }}</h4>
    <div class="card-body">
      <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }">
        <div class="form-group mb-3">
          <label>{{ t('name') }}</label>
          <Field
            v-model="data.name"
            name="name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.name }"
          />
          <div class="invalid-feedback">{{ errors.name }}</div>
        </div>

        <div class="form-group mb-3">
          <label>IP</label>
          <Field
            v-model="data.ip"
            name="ip"
            type="text"
            class="form-control"
            placeholder="192.168.0.1"
            :class="{ 'is-invalid': errors.ip }"
          />
          <div class="invalid-feedback">{{ errors.ip }}</div>
        </div>

        <div class="form-group mb-3">
          <label for="tags">{{ t('tags') }}</label>
          <TagInput @change="onTagsChange($event.target.value)" :initialValue="data.tags" />
        </div>

        <div class="form-group mb-3">
          <button class="btn btn-primary" :disabled="isSubmitting">
            <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
            {{ t('save') }}
          </button>
        </div>
      </Form>
    </div>
  </div>
  <div class="card mt-3 border border-danger">
    <h4 class="card-header text-danger">{{ t('delete_header') }}</h4>
    <div class="card-body">
      <p>{{ t('delete_warning') }}</p>
      <button @click="deleteServer" class="btn btn-danger">{{ t('delete') }}</button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  edit: Edit
  name: Name
  server_updated: 'Server updated'
  tags: Tags
  save: Save
  delete_header: Delete server
  delete_warning: 'Warning: this action is irreversible!'
  delete_server_confirmation: 'Are you sure you want to delete this server?'
  delete: Delete
  schema:
    name: 'Name is required'
    ip: 'Invalid IP address'
ru:
  edit: Редактировать
  name: Имя
  server_updated: 'Сервер обновлен'
  tags: Теги
  save: Сохранить
  delete_header: Удалить сервер
  delete_warning: 'Внимание: это действие нельзя отменить!'
  delete_server_confirmation: 'Вы уверены, что хотите удалить этот сервер?'
  delete: Удалить
  schema:
    name: 'Имя обязательно'
    ip: 'Неверный IP-адрес'
</i18n>

<script setup lang="ts">
import { Form, Field } from 'vee-validate'
import * as yup from 'yup'
import { useAllStores } from '@/store'
import TagInput from '@/components/TagInput.vue'
import { useMyFetch } from '@/utils'
import ipRegex from 'ip-regex'
import { handleError } from '@/utils/errors'

const { alertStore, t, router, authStore, route } = useAllStores()
const server_id = route.params.server_id
const { data, error } = await useMyFetch(`servers/detail/${server_id}`).json()
await handleError(error, router, authStore, alertStore, 'servers:list')

const schema = yup.object({
  name: yup.string().required(t('schema.name')),
  ip: yup
    .string()
    .matches(ipRegex({ includeBoundaries: true }), {
      excludeEmptyString: true,
      message: t('schema.ip')
    })
    .nullable()
})

function onTagsChange(value: string) {
  data.value.tags = value.split(',').filter((tag: string) => tag)
}

async function onSubmit() {
  const { error } = await useMyFetch(`servers/${server_id}/edit`).post(data.value).json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
  } else {
    alertStore.success(t('server_updated'))
    await router.push({ name: 'servers:list' })
  }
}

async function deleteServer() {
  if (confirm(t('delete_server_confirmation'))) {
    const { error } = await useMyFetch(`servers/${server_id}/delete`).delete().json()
    if (error.value) {
      await handleError(error, router, authStore, alertStore)
    } else {
      await router.push({ name: 'servers:list' })
    }
  }
}
</script>
